import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Eye, EyeOff, Trash2, Archive, X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const KNOWN_CATEGORIES = ["Entrées", "Sides", "Appetizers", "Desserts"];
const inputCls = "rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-gold w-full";

const blankItem = { category: "Entrées", name: "", description: "", price: "", active: true, display_order: 0 };

function statusOf(it) {
  if (it.archived) return { label: "Archived", cls: "bg-slate-200 text-slate-600" };
  if (it.active === false) return { label: "Hidden", cls: "bg-amber-100 text-amber-800" };
  return { label: "Active", cls: "bg-green-100 text-green-800" };
}

export default function MenuManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [editing, setEditing] = useState(null);   // item object or blankItem (add)
  const [removing, setRemoving] = useState(null);  // item pending delete confirmation
  const [settings, setSettings] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/menu").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/content/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = Array.from(new Set([...KNOWN_CATEGORIES, ...items.map((i) => i.category)]));

  const shown = items.filter((it) => {
    if (filter === "Hidden / Archived") return it.archived || it.active === false;
    if (it.archived) return false;
    if (filter === "All") return true;
    return it.category === filter;
  });

  const saveItem = async (item) => {
    try {
      const payload = {
        category: item.category, name: item.name, description: item.description || "",
        price: item.price || "", active: !!item.active, display_order: Number(item.display_order) || 0,
      };
      if (item.id) await api.patch(`/admin/menu/${item.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success(item.id ? "Item updated" : "Item added");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (it) => {
    try {
      await api.patch(`/admin/menu/${it.id}`, { active: !it.active });
      toast.success(it.active ? "Item hidden" : "Item shown");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const archiveItem = async (it) => {
    try {
      await api.patch(`/admin/menu/${it.id}`, { archived: true, active: false });
      toast.success("Item archived (kept for order history)");
      setRemoving(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unarchiveItem = async (it) => {
    try {
      await api.patch(`/admin/menu/${it.id}`, { archived: false, active: true });
      toast.success("Item restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteItem = async (it) => {
    try {
      await api.delete(`/admin/menu/${it.id}`);
      toast.success("Item removed");
      setRemoving(null);
      load();
    } catch (e) {
      // 409 => has order history; keep modal open so owner can archive instead
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveAddonPrices = async () => {
    try {
      await api.patch("/admin/settings", {
        additional_side_price: Number(settings.additional_side_price) || 0,
        dessert_price: Number(settings.dessert_price) || 0,
      });
      toast.success("Add-on prices saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="menu-management" className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-serif text-xl text-charcoal">Meal Prep Menu Management</h3>
        <button data-testid="menu-add-item-btn" onClick={() => setEditing({ ...blankItem })}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-charcoal hover:bg-gold-bright">
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      {/* Add-on flat pricing (separate from per-item / package pricing) */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-bone/50 p-4">
        <div className="text-sm font-semibold text-charcoal mb-2">Add-on Pricing (public page)</div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">Additional Side ($ each)
            <input data-testid="menu-addon-additional-price" type="number" step="0.01" className={`${inputCls} mt-1 w-36`}
              value={settings.additional_side_price ?? ""} onChange={(e) => setSettings({ ...settings, additional_side_price: e.target.value })} />
          </label>
          <label className="text-sm">Dessert ($ each)
            <input data-testid="menu-addon-dessert-price" type="number" step="0.01" className={`${inputCls} mt-1 w-36`}
              value={settings.dessert_price ?? ""} onChange={(e) => setSettings({ ...settings, dessert_price: e.target.value })} />
          </label>
          <button data-testid="menu-addon-save" onClick={saveAddonPrices}
            className="inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-sm font-semibold text-white">
            <Save className="w-4 h-4" /> Save Prices
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap gap-2">
        {["All", ...categories, "Hidden / Archived"].map((c) => (
          <button key={c} data-testid={`menu-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            onClick={() => setFilter(c)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${filter === c ? "bg-charcoal text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Items table */}
      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div> :
        shown.length === 0 ? <div className="py-12 text-center text-slate-400" data-testid="menu-empty">No items in this view.</div> : (
          <div className="mt-4 overflow-x-auto thin-scroll rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Item</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((it) => {
                  const st = statusOf(it);
                  return (
                    <tr key={it.id} data-testid={`menu-item-row-${it.id}`} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-charcoal">{it.name}</div>
                        {it.description && <div className="text-xs text-slate-400 max-w-xs truncate">{it.description}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{it.category}</td>
                      <td className="px-4 py-3 text-slate-600">{it.price || "—"}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${st.cls}`}>{st.label}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {it.archived ? (
                            <button data-testid={`menu-restore-${it.id}`} onClick={() => unarchiveItem(it)} title="Restore"
                              className="rounded-lg p-2 text-emerald hover:bg-emerald/10"><Archive className="w-4 h-4" /></button>
                          ) : (
                            <>
                              <button data-testid={`menu-edit-${it.id}`} onClick={() => setEditing(it)} title="Edit"
                                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
                              <button data-testid={`menu-toggle-${it.id}`} onClick={() => toggleActive(it)} title={it.active ? "Hide" : "Show"}
                                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100">{it.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                              <button data-testid={`menu-remove-${it.id}`} onClick={() => setRemoving(it)} title="Remove"
                                className="rounded-lg p-2 text-crimson hover:bg-crimson/10"><Trash2 className="w-4 h-4" /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {editing && <ItemModal item={editing} categories={categories} onSave={saveItem} onClose={() => setEditing(null)} />}
      {removing && <RemoveModal item={removing} onDelete={deleteItem} onArchive={archiveItem} onClose={() => setRemoving(null)} />}
    </div>
  );
}

function ItemModal({ item, categories, onSave, onClose }) {
  const [form, setForm] = useState({ ...item });
  const [customCat, setCustomCat] = useState(!categories.includes(item.category) && item.category ? item.category : "");
  const useCustom = form.category === "__custom__";
  const submit = () => {
    const category = useCustom ? customCat.trim() : form.category;
    if (!category) { toast.error("Please choose or enter a category."); return; }
    if (!form.name.trim()) { toast.error("Item name is required."); return; }
    onSave({ ...form, category });
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="menu-item-modal">
      <div className="absolute inset-0 bg-charcoal/70" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-charcoal text-white px-6 py-4 flex items-center justify-between">
          <h3 className="font-serif text-lg">{item.id ? "Edit Item" : "Add Item"}</h3>
          <button data-testid="menu-item-cancel-x" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-3">
          <label className="block text-sm font-medium text-charcoal">Item Name
            <input data-testid="menu-item-name" className={`${inputCls} mt-1`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="block text-sm font-medium text-charcoal">Description / Caption
            <textarea data-testid="menu-item-description" rows={2} className={`${inputCls} mt-1`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <label className="block text-sm font-medium text-charcoal">Category
            <select data-testid="menu-item-category" className={`${inputCls} mt-1`} value={useCustom ? "__custom__" : form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__custom__">+ New category…</option>
            </select>
          </label>
          {useCustom && (
            <input data-testid="menu-item-category-custom" className={inputCls} placeholder="New category name"
              value={customCat} onChange={(e) => setCustomCat(e.target.value)} />
          )}
          <label className="block text-sm font-medium text-charcoal">Price (optional)
            <input data-testid="menu-item-price" className={`${inputCls} mt-1`} placeholder="e.g. $14 or leave blank" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          </label>
          <div className="flex items-center gap-4">
            <label className="block text-sm font-medium text-charcoal flex-1">Display Order
              <input data-testid="menu-item-display-order" type="number" className={`${inputCls} mt-1`} value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} />
            </label>
            <label className="flex items-center gap-2 text-sm text-charcoal mt-5">
              <input data-testid="menu-item-active" type="checkbox" className="w-4 h-4 accent-gold" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active / Visible
            </label>
          </div>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button data-testid="menu-item-cancel" onClick={onClose} className="flex-1 rounded-full border border-slate-300 px-5 py-2.5 font-semibold text-charcoal hover:bg-bone">Cancel</button>
          <button data-testid="menu-item-save" onClick={submit} className="flex-1 rounded-full bg-gold px-5 py-2.5 font-semibold text-charcoal hover:bg-gold-bright">Save Item</button>
        </div>
      </div>
    </div>
  );
}

function RemoveModal({ item, onDelete, onArchive, onClose }) {
  const hasHistory = item.used_in_orders;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="menu-delete-modal">
      <div className="absolute inset-0 bg-charcoal/70" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-charcoal text-white px-6 py-4"><h3 className="font-serif text-lg">Remove Menu Item</h3></div>
        <div className="p-6 text-sm text-charcoal">
          {hasHistory ? (
            <p data-testid="menu-delete-history-note">
              <strong>{item.name}</strong> exists in previous orders and cannot be permanently deleted.
              It can be hidden/archived instead so historical orders stay intact.
            </p>
          ) : (
            <p>Are you sure you want to remove <strong>{item.name}</strong> from the Meal Prep menu?</p>
          )}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button data-testid="menu-delete-cancel" onClick={onClose} className="flex-1 rounded-full border border-slate-300 px-5 py-2.5 font-semibold text-charcoal hover:bg-bone">Cancel</button>
          {hasHistory ? (
            <button data-testid="menu-archive-instead" onClick={() => onArchive(item)} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 font-semibold text-white hover:bg-amber-600">
              <Archive className="w-4 h-4" /> Archive Instead
            </button>
          ) : (
            <button data-testid="menu-delete-confirm" onClick={() => onDelete(item)} className="flex-1 rounded-full bg-crimson px-5 py-2.5 font-semibold text-white hover:opacity-90">Remove Item</button>
          )}
        </div>
      </div>
    </div>
  );
}
